import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, animate, useReducedMotion } from "framer-motion";
import { imgProps } from "./media";

const EASE = [0.2, 0.7, 0.2, 1];

/* Reveal: opacity + translateY(16px), 400ms, una sola volta (framer smette di
   osservare dopo la prima comparsa). Nessun filtro o variazione di luminosità. */
export function Reveal({ children, delay = 0, y = 16, className = "", style, ...rest }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      style={style}
      {...rest}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: reduce ? 0 : 0.4, ease: "easeOut", delay: reduce ? 0 : delay }}
    >
      {children}
    </motion.div>
  );
}

/* Masked line-by-line reveal: each child string becomes a masked rising line.
   A plain space is kept between lines so bots read one continuous sentence. */
export function Lines({ lines, as: Tag = "h1", className = "", lineClass = "", baseDelay = 0, accentLast = false }) {
  const reduce = useReducedMotion();
  return (
    <Tag className={className}>
      {lines.map((l, i) => (
        <React.Fragment key={i}>
          <span className={`hl ${lineClass}`}>
            <motion.span
              className="hl-in"
              initial={reduce ? false : { y: "112%" }}
              animate={{ y: "0%" }}
              transition={{ duration: reduce ? 0 : 1.05, ease: EASE, delay: reduce ? 0 : baseDelay + i * 0.14 }}
            >
              {accentLast && i === lines.length - 1 ? <span className="au">{l}</span> : l}
            </motion.span>
          </span>
          {i < lines.length - 1 ? " " : null}
        </React.Fragment>
      ))}
    </Tag>
  );
}

/* Same but triggered on scroll into view (once) */
export function LinesInView({ lines, as: Tag = "h2", className = "", accentLast = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const reduce = useReducedMotion();
  return (
    <Tag className={className} ref={ref}>
      {lines.map((l, i) => (
        <React.Fragment key={i}>
          <span className="hl">
            <motion.span
              className="hl-in"
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
              transition={{ duration: reduce ? 0 : 0.4, ease: "easeOut", delay: reduce ? 0 : i * 0.13 }}
            >
              {accentLast && i === lines.length - 1 ? <span className="au">{l}</span> : l}
            </motion.span>
          </span>
          {i < lines.length - 1 ? " " : null}
        </React.Fragment>
      ))}
    </Tag>
  );
}

export function Counter({ to, plain, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const reduce = useReducedMotion();
  const [v, setV] = useState(0);
  const done = useRef(false);
  useEffect(() => {
    if (!inView || to == null || done.current) return;
    done.current = true;
    if (reduce) {
      setV(to);
      return;
    }
    const c = animate(0, to, { duration: 1.6, ease: EASE, onUpdate: (x) => setV(Math.round(x)) });
    return () => c.stop();
  }, [inView, to, reduce]);
  return (
    <p className={`big ${className}`} ref={ref} data-testid={`stat-${to ?? plain}`}>
      {to == null ? plain : v.toLocaleString("it-IT")}
    </p>
  );
}

/* Slow editorial marquee row; dir = 1 | -1 */
export function MarqueeRow({ items, dir = 1, testid }) {
  const seq = (hidden) =>
    items.map(([slug, name]) => (
      <div className="lgt" key={slug + (hidden ? "-h" : "")} aria-hidden={hidden || undefined}>
        <div className="lg">
          <img
            {...imgProps(`/assets/clienti/${slug}.png`, { sizes: "200px" })}
            alt={hidden ? "" : name}
            title={name}
          />
        </div>
      </div>
    ));
  return (
    <div className={`track ${dir === -1 ? "rev" : ""}`} data-testid={testid}>
      {seq(false)}
      {seq(true)}
    </div>
  );
}

/* Video cover: poster sempre visibile finché (e se) il video non carica.
   Il video viene montato SOLO dopo che l'immagine di anteprima è comparsa
   (posterLoaded): su telefono non compete con l'LCP.
   No video con reduced motion, data saver o connessione lenta (2g/3g).
   Su iOS la riproduzione parte anche via play() dopo il caricamento; se il
   browser la blocca resta il poster, senza messaggi.
   Il video si mette in pausa quando la copertina esce dal viewport (IntersectionObserver). */
export function HeroVideo({ src, poster, frame = false }) {
  const [play, setPlay] = useState(false);
  const [posterLoaded, setPosterLoaded] = useState(false);
  const [loadVideo, setLoadVideo] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const userPausedRef = useRef(false);
  const posterRef = useRef(null);
  const vidRef = useRef(null);
  const boxRef = useRef(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (posterRef.current && posterRef.current.complete) setPosterLoaded(true);
  }, []);
  useEffect(() => {
    if (reduce) return;
    const conn = navigator.connection || navigator.mozConnection || {};
    const slow = conn.saveData || ["slow-2g", "2g", "3g"].includes(conn.effectiveType);
    if (!slow) setPlay(true);
  }, [reduce]);
  /* il video inizia a caricarsi solo dopo che il poster è in pagina */
  useEffect(() => {
    if (!play || !posterLoaded) return undefined;
    const t = setTimeout(() => setLoadVideo(true), 120);
    return () => clearTimeout(t);
  }, [play, posterLoaded]);
  const tryPlay = () => {
    const v = vidRef.current;
    if (v && v.paused && !userPausedRef.current) {
      const pr = v.play();
      if (pr && pr.catch) pr.catch(() => {});
    }
  };
  /* Pausa/riproduzione manuale: rispetta la scelta dell'utente anche
     quando la copertina rientra nel viewport. */
  const toggleVideo = () => {
    const v = vidRef.current;
    if (!v) return;
    if (v.paused) {
      userPausedRef.current = false;
      setUserPaused(false);
      const pr = v.play();
      if (pr && pr.catch) pr.catch(() => {});
    } else {
      userPausedRef.current = true;
      setUserPaused(true);
      v.pause();
    }
  };
  useEffect(() => {
    if (!play || !boxRef.current) return undefined;
    const v = vidRef.current;
    const io = new IntersectionObserver(
      ([e]) => {
        if (!v) return;
        if (e.isIntersecting && !userPausedRef.current) {
          const pr = v.play();
          if (pr && pr.catch) pr.catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.01 }
    );
    io.observe(boxRef.current);
    return () => io.disconnect();
  }, [play]);
  return (
    <div
      ref={boxRef}
      style={{ position: "absolute", inset: 0, overflow: "hidden", background: "var(--blue)" }}
      data-testid={frame ? "hero-video-frame" : "hero-video"}
    >
      <div aria-hidden="true" style={{ position: "absolute", inset: frame ? 0 : "-5% 0" }}>
        <img
          ref={posterRef}
          {...imgProps(poster, { sizes: "100vw", priority: true })}
          alt=""
          onLoad={() => setPosterLoaded(true)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
        {loadVideo && (
          <video
            ref={vidRef}
            src={src}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedMetadata={tryPlay}
            onCanPlay={tryPlay}
            onError={() => setLoadVideo(false)}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
      </div>
      {!frame && (
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(rgba(8,52,110,0.74), rgba(8,52,110,0.74))" }} />
      )}
      {!frame && loadVideo && (
        <button
          type="button"
          className="vid-toggle"
          onClick={toggleVideo}
          aria-label={userPaused ? "Riproduci il video di sfondo" : "Metti in pausa il video di sfondo"}
          data-testid="video-toggle"
        >
          {userPaused ? "Riproduci" : "Metti in pausa"}
        </button>
      )}
    </div>
  );
}

/* Static photo cover (senza parallasse): immagine reale con srcset WebP e
   priorità alta, perché è la copertina della pagina (elemento LCP). */
export function ParallaxPhoto({ url, veil = true, className = "" }) {
  return (
    <div className={className} style={{ position: "absolute", inset: 0, overflow: "hidden" }} aria-hidden="true">
      <img
        {...imgProps(url, { sizes: "100vw", priority: true })}
        alt=""
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 35%" }}
      />
      {veil && <div className="hero-veil" />}
    </div>
  );
}
